import React from 'react';

import { FormCheckbox } from './FormCheckbox';
import { FormDropdownField } from './FormDropdownField';
import { FormFieldLabelWithInfo } from './FormFieldLabelWithInfo';
import { FormInput } from './FormInput';
import { FormYesNoChoice } from './FormYesNoChoice';
import { formFieldInStack } from './SubsectionForm';
import { PermissionAwareField } from '../PermissionAwareField';

import type {
  FormDataCheckboxSpec,
  FormDataDropdownSpec,
  FormDataFieldRenderContext,
  FormDataFieldSpec,
  FormDataTextSpec,
  FormDataYesNoSpec,
} from './dataFieldSpec';

function wrapPermission(
  spec: { permissionFieldId: string },
  ctx: FormDataFieldRenderContext,
  child: React.ReactNode,
): React.ReactElement {
  return (
    <PermissionAwareField
      fieldId={spec.permissionFieldId}
      requestStage={ctx.requestStage}
      requestId={ctx.requestId}
      showFullDetails={ctx.showFullDetails}
    >
      {child}
    </PermissionAwareField>
  );
}

function effectiveDisabled(
  ctx: FormDataFieldRenderContext,
  fieldKey: string,
): boolean {
  const base = ctx.disabled;
  const extra = ctx.getControlDisabled?.(fieldKey) ?? false;
  return base || extra;
}

function renderText(
  spec: FormDataTextSpec,
  ctx: FormDataFieldRenderContext,
): React.ReactNode {
  const d = effectiveDisabled(ctx, spec.fieldKey);

  if (spec.helpInfoKey && ctx.help) {
    const a11y = ctx.help.getA11y(spec.helpInfoKey);
    return (
      <>
        <FormFieldLabelWithInfo
          onInfo={() => ctx.help!.openKey(spec.helpInfoKey!)}
          infoA11yHint={a11y.hint}
          a11yDescription={a11y.description}
        >
          {spec.label}
        </FormFieldLabelWithInfo>
        <FormInput
          label={spec.label}
          hideLabel
          value={String(ctx.formData[spec.fieldKey] ?? '')}
          onChangeText={v => ctx.updateFormData(spec.fieldKey, v)}
          placeholder={spec.placeholder}
          multiline={spec.multiline}
          numberOfLines={spec.numberOfLines}
          maxLength={spec.maxLength}
          disabled={d}
          readOnlyMode={ctx.disabled}
          webMultilineAutoSize={spec.webMultilineAutoSize}
          webMultilineMinHeight={spec.webMultilineMinHeight}
          webMultilineMaxHeight={spec.webMultilineMaxHeight}
          testID={spec.testID}
          containerStyle={[formFieldInStack, spec.containerStyle]}
        />
      </>
    );
  }

  return (
    <FormInput
      label={spec.label}
      hideLabel={spec.hideLabel}
      value={String(ctx.formData[spec.fieldKey] ?? '')}
      onChangeText={v => ctx.updateFormData(spec.fieldKey, v)}
      placeholder={spec.placeholder}
      multiline={spec.multiline}
      numberOfLines={spec.numberOfLines}
      maxLength={spec.maxLength}
      required={spec.required}
      disabled={d}
      readOnlyMode={ctx.disabled}
      webMultilineAutoSize={spec.webMultilineAutoSize}
      webMultilineMinHeight={spec.webMultilineMinHeight}
      webMultilineMaxHeight={spec.webMultilineMaxHeight}
      testID={spec.testID}
      containerStyle={[formFieldInStack, spec.containerStyle]}
    />
  );
}

function renderYesNo(
  spec: FormDataYesNoSpec,
  ctx: FormDataFieldRenderContext,
): React.ReactNode {
  const d = effectiveDisabled(ctx, spec.fieldKey);
  return (
    <FormYesNoChoice
      label={spec.label}
      value={String(ctx.formData[spec.fieldKey] ?? '')}
      onSelect={v => ctx.updateFormData(spec.fieldKey, v)}
      required={spec.required}
      disabled={d}
      readOnlyMode={ctx.disabled}
      testID={spec.testID}
      inline={spec.inline}
      containerStyle={formFieldInStack}
    />
  );
}

function renderCheckbox(
  spec: FormDataCheckboxSpec,
  ctx: FormDataFieldRenderContext,
): React.ReactNode {
  const d = effectiveDisabled(ctx, spec.fieldKey);
  const raw = ctx.formData[spec.fieldKey];
  const value =
    spec.coerceBoolean === true ? raw === true || raw === 'true' : Boolean(raw);
  return (
    <FormCheckbox
      label={spec.label}
      value={value}
      onValueChange={d ? () => {} : v => ctx.updateFormData(spec.fieldKey, v)}
      disabled={d}
      readOnlyMode={ctx.disabled}
      testID={spec.testID}
      containerStyle={formFieldInStack}
    />
  );
}

function renderDropdown(
  spec: FormDataDropdownSpec,
  ctx: FormDataFieldRenderContext,
): React.ReactNode {
  const d = effectiveDisabled(ctx, spec.fieldKey);
  const onInfo =
    spec.onInfoKey && ctx.help
      ? () => ctx.help!.openKey(spec.onInfoKey!)
      : undefined;
  const a11y =
    spec.onInfoKey && ctx.help ? ctx.help.getA11y(spec.onInfoKey) : undefined;

  return (
    <FormDropdownField
      label={spec.label}
      onInfo={onInfo}
      infoA11yHint={a11y?.hint}
      a11yDescription={a11y?.description}
      value={String(ctx.formData[spec.fieldKey] ?? '')}
      options={[...spec.options]}
      onSelect={v =>
        ctx.updateFormData(spec.fieldKey, Array.isArray(v) ? v[0] : v)
      }
      allowClear={spec.allowClear}
      disabled={d}
      readOnlyMode={ctx.disabled}
      placeholder={spec.placeholder}
      testID={spec.testID}
    />
  );
}

/**
 * Render one declarative `formData` field (PermissionAwareField + control).
 */
export function renderFormDataField(
  spec: FormDataFieldSpec,
  ctx: FormDataFieldRenderContext,
): React.ReactElement {
  let inner: React.ReactNode;
  switch (spec.kind) {
    case 'text':
      inner = renderText(spec, ctx);
      break;
    case 'yesNo':
      inner = renderYesNo(spec, ctx);
      break;
    case 'checkbox':
      inner = renderCheckbox(spec, ctx);
      break;
    case 'dropdown':
      inner = renderDropdown(spec, ctx);
      break;
    default: {
      const _x: never = spec;
      return _x;
    }
  }
  return wrapPermission(spec, ctx, inner);
}

/**
 * Render a list of specs in order (each wrapped with its own PermissionAwareField).
 */
export function renderFormDataFields(
  specs: readonly FormDataFieldSpec[],
  ctx: FormDataFieldRenderContext,
): React.ReactElement[] {
  return specs.map((spec, i) => (
    <React.Fragment key={`${spec.kind}-${spec.permissionFieldId}-${i}`}>
      {renderFormDataField(spec, ctx)}
    </React.Fragment>
  ));
}
